import { DataGridPro, GridRowParams, GridSelectionModel } from '@mui/x-data-grid-pro';
import { useCustomSearchParams } from 'common/hooks/customSearchParams';
import * as React from 'react';
import { UseQueryResult } from '@tanstack/react-query';
import { TerminalCredential, TerminalCredentialResponse } from 'services/clients/credentials';
import { ActionHandler } from '../../actions';
import { getColumns } from './columns/Columns';
import { CREDENTIALS_QUERY_PARAMS } from './filtering';

interface Props {
  credentialsQuery: UseQueryResult<TerminalCredentialResponse>;
  onSelectionChange: (selection: GridSelectionModel) => void;
  onAction: ActionHandler;
  onCreate: (credential: TerminalCredential) => void;
}

export const CredentialsTable: React.FC<Props> = (props) => {
  const { searchParams, setSearchParams } = useCustomSearchParams(CREDENTIALS_QUERY_PARAMS);

  const { credentialsQuery, onAction, onCreate, onSelectionChange } = props;

  const columns = React.useMemo(() => getColumns(onAction, onCreate), [onAction, onCreate]);

  return (
    <DataGridPro
      autoHeight
      columns={columns}
      rows={credentialsQuery.data?.results || []}
      checkboxSelection
      rowCount={credentialsQuery.data?.count || 0}
      rowHeight={62}
      pagination
      paginationMode="server"
      page={searchParams.page - 1}
      onPageChange={(page: number) => {
        setSearchParams({ page: page + 1 });
      }}
      pageSize={searchParams.pageSize}
      onPageSizeChange={(pageSize: number) => setSearchParams({ pageSize })}
      rowsPerPageOptions={[25, 50, 75, 100]}
      loading={credentialsQuery.isFetching}
      error={credentialsQuery.error}
      disableSelectionOnClick
      onRowClick={(params: GridRowParams<TerminalCredential>) => {
        onAction('update', [params.row.id]);
      }}
      sortingMode="server"
      onSortModelChange={(model) => {
        if (model.length === 0) {
          setSearchParams({ sortOrder: null, sortBy: null });
        } else {
          setSearchParams({ sortOrder: model[0].sort, sortBy: model[0].field });
        }
      }}
      onSelectionModelChange={onSelectionChange}
      className="min-h-[400px]"
      classes={{
        cellCheckbox: 'flex items-center justify-center',
        columnHeaders: 'bg-white shadow-sm border-none',
        columnHeaderCheckbox: 'bg-white shadow-sm py-4',
        root: 'bg-white border-none',
        row: 'border-b border-gray-100',
        main: 'bg-white',
        footerContainer: 'flex flex-row-reverse flex-grow border-none bg-white',
      }}
      sx={{
        '.MuiDataGrid-cellCheckbox': {
          backgroundColor: 'transparent',
        },
        '.MuiDataGrid-cell': {
          borderBottom: 'none',
        },
      }}
      disableColumnReorder
    />
  );
};
